import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../App";
import { formatDate } from "../../../hooks/Method";
import Barcode from "react-barcode/lib/react-barcode";

const GeneralPrinterReceipt = ({ data }) => {
  const { settings } = useContext(AppContext);
  const [store, setStore] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    setStore({ ...data.store });
    return () => {};
  }, [data]);

  return data.id && store.id ? (
    <div
      style={{
        maxWidth: "210mm", // A4 width
        width: "100%",
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: "14px",
        lineHeight: 1.6,
        color: "#333",
        backgroundColor: "#fff",
        padding: "20px",
        margin: "0 auto", // Center the content
        boxSizing: "border-box",
      }}
    >
      {/* Store Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "8px",
          paddingBottom: "12px",
          borderBottom: "2px solid #ddd",
        }}
      >
        {/* ----- Branding Section ----- */}
        <div>
          {store.logo && store.showLogoOnReceipt === true && (
            <div
              style={{
                marginBottom: "15px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src={store.logo}
                alt="store_logo"
                style={{
                  maxHeight: "120px",
                  maxWidth: "250px",
                  objectFit: "contain",
                }}
              />
            </div>
          )}
          {store.showStoreNameOnReceipt && (
            <>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  letterSpacing: "0.5px",
                  color: "#222",
                  marginBottom: "8px",
                  textAlign: "center",
                }}
              >
                {store.storeName}
              </div>
              {store.localName && (
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    color: "#444",
                    marginBottom: "6px",
                    textAlign: "center",
                  }}
                >
                  {store.localName}
                </div>
              )}
            </>
          )}
          {store.showTaglineOnReceipt && (
            <div
              style={{
                fontSize: "16px",
                fontWeight: "500",
                fontStyle: "italic",
                letterSpacing: "0.5px",
                color: "#444",
                padding: "0px 10px",
                border: "2px dashed #ddd",
                textAlign: "center",
                width: "fit-content",
                margin: "0 auto",
              }}
            >
              {store.tagline}
            </div>
          )}
        </div>

        {/* ----- Address Section ----- */}
        {(store.showAddressOnReceipt || store.showPhoneOnReceipt) && (
          <div
            style={{
              display: "grid",
              justifyContent: "center",
              fontSize: "15px",
              color: "#666",
              lineHeight: 1.4,
              marginTop: "8px",
              gap: "4px",
            }}
          >
            <div>
              {store.showAddressOnReceipt && (
                <>
                  {store.address && (
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      📌 {store.address}
                    </span>
                  )}
                  <div>
                    {store.city ? `, ${store.city}` : ""}
                    {store.state ? `, ${store.state}` : ""}
                    {store.zip ? `, ${store.zip}` : ""}
                  </div>
                </>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              {store.showPhoneOnReceipt && store.phone && (
                <span>📞 {store.phone}</span>
              )}
              {store.showEmailOnReceipt && store.email && (
                <span>✉️ {store.email}</span>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              {store.website && <span>🌐 {store.website}</span>}
              {store.socialMedia && (
                <span style={{ whiteSpace: "pre-wrap" }}>
                  {store.socialMedia}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginBottom: "10px",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: "10px", fontSize: "18px" }}>
            <span style={{ fontWeight: "500" }}>{t("invoice")}:</span>
            <span>#{data.id}</span>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <span style={{ fontWeight: "500" }}>{t("date")}:</span>
            <span>{formatDate(data.sellDate)}</span>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <span style={{ fontWeight: "500" }}>{t("customer")}:</span>
            <span>
              {data.cstmr ? data.cstmr.fullName : t("walkInCustomer")}
            </span>
          </div>
          {data.cstmr && (
            <div style={{ display: "flex", gap: "10px" }}>
              <span style={{ fontWeight: "500" }}>{t("phone")}:</span>
              <span>{data.cstmr.phone}</span>
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          {data.id && (
            <Barcode
              value={data.id.toString()}
              displayValue={true}
              height={50}
              width={2}
              fontSize={14}
            />
          )}
        </div>
      </div>

      {/* Items Table */}
      <div
        style={{
          marginBottom: "10px",
          borderBottom: "2px solid #ddd",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "0.10fr 2.4fr 0.50fr 1fr 1fr",
            gap: "12px",
            fontWeight: "600",
            textTransform: "uppercase",
            marginBottom: "12px",
            color: "#555",
            borderTop: "1px solid #eee",
            borderBottom: "1px solid #eee",
          }}
        >
          <div>#</div>
          <div>{t("itemName")}</div>
          <div style={{ textAlign: "center" }}>{t("qty")}</div>
          <div style={{ textAlign: "right" }}>{t("unitPrice")}</div>
          <div style={{ textAlign: "right" }}>{t("totalPrice")}</div>
        </div>

        {data.sellItems?.map((item, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "0.10fr 2.4fr 0.50fr 1fr 1fr",
              gap: "12px",
              marginBottom: "3px",
              color: "#333",
              paddingBottom: "3px",
              fontSize: "14px",
              borderBottom: `${
                data.sellItems.length > i + 1 ? "1" : "0"
              }px solid #eee`,
            }}
          >
            <div>{i + 1}</div>
            <div>{item.product.label}</div>
            <div style={{ textAlign: "center" }}>{item.qty}</div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol} {item.price.toFixed(settings.decimal)}
            </div>
            <div style={{ textAlign: "right" }}>
              {settings.curSymbol}{" "}
              {(item.qty * item.price).toFixed(settings.decimal)}
            </div>
          </div>
        ))}
      </div>

      {/* Totals Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
        }}
      >
        <div
          style={{
            display: "grid",
            fontSize: "12px",
            color: "#666",
            lineHeight: 1.4,
            paddingTop: "15px",
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            maxWidth: "100%",
          }}
        >
          {data.note && (
            <span
              style={{
                borderBottom: "1px solid #ddd",
                paddingBottom: "10px",
                marginBottom: "10px",
              }}
            >
              {data.note}
            </span>
          )}
          <span>{settings.salesTermForReceipt}</span>
        </div>
        {/* --------- Accounts ---------  */}
        <div
          style={{
            display: "grid",
            gap: "2px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#555" }}>{t("subtotal")}:</span>
            <span style={{ color: "#333" }}>
              {settings.curSymbol} {data.subtotal.toFixed(settings.decimal)}
            </span>
          </div>

          {data.tax > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #ddd",
              }}
            >
              <span style={{ color: "#555" }}>
                {t("tax")} ({data.taxPct}%):
              </span>
              <span style={{ color: "#333" }}>
                +{settings.curSymbol} {data.tax.toFixed(settings.decimal)}
              </span>
            </div>
          )}

          {data.disAmt > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #ddd",
              }}
            >
              <span style={{ color: "#555" }}>
                {t("discount")}{" "}
                {data.discountType === 1 && `(${data.discount}%)`}:
              </span>
              <span style={{ color: "#333" }}>
                (-) {settings.curSymbol} {data.disAmt.toFixed(settings.decimal)}
              </span>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "600",
              paddingTop: "5px",
              borderTop: "1px solid #ddd",
              color: "#222",
            }}
          >
            <span>{t("totalPay")}:</span>
            <span>
              {settings.curSymbol} {data.totalPay.toFixed(settings.decimal)}
            </span>
          </div>

          {data.payments.map((pay, ind) => (
            <div
              key={ind}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <span style={{ color: "#555" }}>{pay.payMethod?.title}:</span>
              <span style={{ color: "#333" }}>
                {settings.curSymbol} {pay.amount.toFixed(settings.decimal)}
              </span>
            </div>
          ))}

          {data.received > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#555" }}>{t("totalReceived")}:</span>
              <span style={{ color: "#333" }}>
                {settings.curSymbol} {data.received.toFixed(settings.decimal)}
              </span>
            </div>
          )}

          {data.creditAmt > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "#d00",
              }}
            >
              <span>{t("useCredit")}:</span>
              <span>
                {settings.curSymbol} {data.creditAmt.toFixed(settings.decimal)}
              </span>
            </div>
          )}

          {data.dueAmt > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "#d00",
              }}
            >
              <span>{t("due")}:</span>
              <span>
                {settings.curSymbol} {data.dueAmt.toFixed(settings.decimal)}
              </span>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "600",
              paddingTop: "4px",
              borderTop: "1px solid #ddd",
              color: "#222",
            }}
          >
            <span>{data.changeAddedCredit ? t("credit") : t("change")}:</span>
            <span>
              {settings.curSymbol} {data.change.toFixed(settings.decimal)}
            </span>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default GeneralPrinterReceipt;
